import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, inject } from '@angular/core';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

type ChargePolicyType = 'FixedDeny' | 'PointDeduct' | 'UpgradeHint' | 'PurchaseAddon' | 'PaidExtension';
type RiskStatus = 'Normal' | 'Warning' | 'Danger';
type QuotaUsageStatus = 'Normal' | 'HighUsage' | 'Full' | 'OverLimit';

interface PointBalanceSummary {
  ailePoint: number | null;
  uuponPoint: number | null;
  aipoolTenantId: string | null;
  lastSyncTime?: number | null;
}

interface RiskSummary {
  overLimitCount?: number;
  highUsageCount?: number;
  lowBalanceAlert?: boolean;
  status?: RiskStatus;
}

interface UsageSummary {
  quotaItemCount?: number;
  overLimitCount?: number;
  deviationCount?: number;
}

interface QuotaUsageItem {
  itemId?: string;
  quotaCode?: string;
  quotaName?: string;
  categoryCode?: string;
  categoryName?: string;
  freeLimitValue?: string;
  limitValue?: string | null;
  usedValue?: string | null;
  remainingValue?: string | null;
  usageRate?: number | null;
  unit?: string;
  pointDeductUnitPrice?: string | null;
  chargePolicyType?: ChargePolicyType | null;
  chargePolicyLabel?: string | null;
  status?: QuotaUsageStatus;
  overLimit?: boolean;
  displayHint?: string | null;
  lastUpdateTime?: number | null;
}

interface TenantQuotaUsageDetail {
  tenantId?: string;
  tenantName?: string;
  tenantType?: string;
  planId?: string;
  planName?: string;
  scopeType?: string;
  snapshotTime?: number;
  pointBalance?: Partial<PointBalanceSummary> | null;
  riskSummary?: RiskSummary | null;
  usageSummary?: UsageSummary | null;
  items?: QuotaUsageItem[];
}

@Component({
  selector: 'tenant-quota-usage',
  standalone: true,
  imports: [CommonModule, NzCardModule, NzEmptyModule, NzProgressModule, NzSpinModule, I18nPipe, TestIdDirective],
  templateUrl: './tenant-quota-usage.component.html',
  styleUrls: ['./tenant-quota-usage.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TenantQuotaUsageComponent implements OnInit {
  private static readonly POINT_DEDUCT_POLICY: ChargePolicyType = 'PointDeduct';
  private static readonly BROADCAST_LIMIT_CODE = 'BROADCAST_LIMIT';

  private readonly http = inject(HttpService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  @Input() tenantId = '';
  @Input() tenantName = '';

  loading = false;
  pointBalance: PointBalanceSummary = {
    ailePoint: null,
    uuponPoint: null,
    aipoolTenantId: null,
    lastSyncTime: null
  };
  detail: TenantQuotaUsageDetail | null = null;
  riskSummary: RiskSummary | null = null;
  usageSummary: UsageSummary | null = null;
  quotaItems: QuotaUsageItem[] = [];

  ngOnInit(): void {
    this.loadData();
  }

  get quotaItemCount(): number {
    return this.usageSummary?.quotaItemCount ?? this.quotaItems.length;
  }

  get hasQuotaItems(): boolean {
    return this.quotaItems.length > 0;
  }

  t(key: string, params?: Record<string, unknown>): string {
    return this.i18n.fanyi(key, params);
  }

  getChargePolicyLabel(type?: ChargePolicyType | null): string {
    const labelMap: Record<ChargePolicyType, string> = {
      FixedDeny: this.t('quota.policy.fixed'),
      PointDeduct: this.t('quota.policy.deduct-points'),
      UpgradeHint: this.t('quota.policy.upgrade-prompt'),
      PurchaseAddon: this.t('quota.policy.purchase-extension'),
      PaidExtension: this.t('quota.policy.paid-extension')
    };

    return type ? (labelMap[type] ?? type) : '--';
  }

  formatQuotaLimit(item: QuotaUsageItem): string {
    const value = String(item?.limitValue ?? item?.freeLimitValue ?? '').trim();
    const unit = String(item?.unit ?? '').trim();
    if (!value) {
      return '--';
    }
    return `${value}${unit ? ` ${unit}` : ''}`;
  }

  formatNullableValue(value: string | number | null | undefined, unit?: string | null): string {
    if (value === null || value === undefined || value === '') {
      return '--';
    }
    const normalizedUnit = String(unit ?? '').trim();
    return `${value}${normalizedUnit ? ` ${normalizedUnit}` : ''}`;
  }

  getUsedValueDisplay(item: QuotaUsageItem): string {
    if (item?.quotaCode === TenantQuotaUsageComponent.BROADCAST_LIMIT_CODE) {
      if (item.usedValue === null || item.usedValue === undefined || item.usedValue === '') {
        return '--';
      }
      return this.t('tenant.detail.quota-usage-used-broadcast-this-month', { value: item.usedValue });
    }

    return this.formatNullableValue(item.usedValue, item.unit);
  }

  getQuotaHint(item?: QuotaUsageItem | null): string {
    if (!item) {
      return '';
    }

    const pointDeductHint = this.getPointDeductHint(item);
    if (pointDeductHint) {
      return pointDeductHint;
    }

    return String(item.displayHint ?? '').trim();
  }

  formatUsageRate(rate: number | null | undefined): string {
    if (rate === null || rate === undefined || !Number.isFinite(Number(rate))) {
      return '--';
    }
    return `${Number(rate).toFixed(1)}%`;
  }

  isPointDeductPolicy(item?: QuotaUsageItem | null): boolean {
    return item?.chargePolicyType === TenantQuotaUsageComponent.POINT_DEDUCT_POLICY;
  }

  getRemainingDisplay(item: QuotaUsageItem): string {
    return this.isPointDeductPolicy(item) ? '--' : this.formatNullableValue(item.remainingValue, item.unit);
  }

  getUsageRateDisplay(item: QuotaUsageItem): string {
    return this.isPointDeductPolicy(item) ? '--' : this.formatUsageRate(item.usageRate);
  }

  getRiskLabel(status?: RiskStatus | null): string {
    const labelMap: Record<RiskStatus, string> = {
      Normal: this.t('tenant.detail.quota-usage-risk-normal'),
      Warning: this.t('tenant.detail.quota-usage-risk-warning'),
      Danger: this.t('tenant.detail.quota-usage-risk-danger')
    };

    return status ? (labelMap[status] ?? status) : '--';
  }

  getStatusLabel(status?: QuotaUsageStatus | null): string {
    const labelMap: Record<QuotaUsageStatus, string> = {
      Normal: this.t('tenant.detail.quota-usage-status-normal'),
      HighUsage: this.t('tenant.detail.quota-usage-status-high-usage'),
      Full: this.t('tenant.detail.quota-usage-status-full'),
      OverLimit: this.t('tenant.detail.quota-usage-status-over-limit')
    };

    return status ? (labelMap[status] ?? status) : '--';
  }

  getUsageStateLabel(item?: QuotaUsageItem | null): string {
    if (!item) {
      return '--';
    }

    if (this.isPointDeductPolicy(item)) {
      if (item.quotaCode === TenantQuotaUsageComponent.BROADCAST_LIMIT_CODE) {
        return this.t('tenant.detail.quota-usage-point-deduct-hint-broadcast');
      }

      return this.t('tenant.detail.quota-usage-point-deduct-hint-default');
    }

    return this.getStatusLabel(item.status);
  }

  getChargePolicyDisplayLabel(item?: QuotaUsageItem | null): string {
    if (!item) {
      return '--';
    }

    return this.getChargePolicyLabel(item.chargePolicyType) || item.chargePolicyLabel || '--';
  }

  getProgressPercent(item?: QuotaUsageItem | null): number {
    const rate = Number(item?.usageRate);
    if (!Number.isFinite(rate) || this.isPointDeductPolicy(item)) {
      return 0;
    }

    return Math.max(0, Math.min(rate, 100));
  }

  hasUsageRate(item?: QuotaUsageItem | null): boolean {
    return !this.isPointDeductPolicy(item) && Number.isFinite(Number(item?.usageRate));
  }

  getStatusColor(status?: QuotaUsageStatus): string {
    if (status === 'OverLimit') return 'red';
    if (status === 'Full' || status === 'HighUsage') return 'orange';
    return 'green';
  }

  getRiskColor(status?: RiskStatus): string {
    if (status === 'Danger') return 'red';
    if (status === 'Warning') return 'orange';
    return 'green';
  }

  getProgressStrokeColor(status?: QuotaUsageStatus): string {
    if (status === 'OverLimit') return '#ff4d4f';
    if (status === 'Full' || status === 'HighUsage') return '#fa8c16';
    return '#52c41a';
  }

  private getPointDeductHint(item: QuotaUsageItem): string {
    if (!this.isPointDeductPolicy(item)) {
      return '';
    }

    const price = String(item.pointDeductUnitPrice ?? '').trim();
    if (!price) {
      return '';
    }

    const quotaCode = String(item.quotaCode ?? '')
      .trim()
      .toUpperCase();
    if (quotaCode === TenantQuotaUsageComponent.BROADCAST_LIMIT_CODE) {
      return this.t('quota.view.point-deduct-hint.broadcast', { price });
    }

    const unit = String(item.unit ?? '')
      .trim()
      .toUpperCase();
    if (unit === 'GB') {
      return this.t('quota.view.point-deduct-hint.storage', { price });
    }
    if (unit === '次') {
      return this.t('quota.view.point-deduct-hint.usage', { price });
    }

    const displayUnit = String(item.unit ?? '').trim();
    if (!displayUnit) {
      return '';
    }

    return this.t('quota.view.point-deduct-hint.default', {
      unit: displayUnit,
      price
    });
  }

  private loadData(): void {
    const tenantId = String(this.tenantId ?? '').trim();
    if (!tenantId) {
      return;
    }

    this.loading = true;
    this.http
      .post(ConstantUtil.ADMIN_QUOTA_TENANT_V1_USAGE_DETAIL, { tenantId })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: res => {
          if (!res?.success || !res?.data) {
            this.resetDetail();
            return;
          }

          const detail = res.data as TenantQuotaUsageDetail;
          this.detail = detail;
          this.pointBalance = {
            ailePoint: this.toNullableNumber(detail.pointBalance?.ailePoint),
            uuponPoint: this.toNullableNumber(detail.pointBalance?.uuponPoint),
            aipoolTenantId: detail.pointBalance?.aipoolTenantId ?? null,
            lastSyncTime: detail.pointBalance?.lastSyncTime ?? null
          };
          this.riskSummary = detail.riskSummary ?? null;
          this.usageSummary = detail.usageSummary ?? null;
          this.quotaItems = Array.isArray(detail.items) ? detail.items : [];
        },
        error: () => {
          this.resetDetail();
        }
      });
  }

  private resetDetail(): void {
    this.detail = null;
    this.riskSummary = null;
    this.usageSummary = null;
    this.quotaItems = [];
    this.pointBalance = {
      ailePoint: null,
      uuponPoint: null,
      aipoolTenantId: null,
      lastSyncTime: null
    };
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const normalized = Number(value);
    return Number.isFinite(normalized) ? normalized : null;
  }
}
